import { formatRemaining } from '../demo/sla.js';
import { initials, refresh, statusText } from '../demo/store.js';
import { isWalkthroughBuild } from '../lib/liveWrites.js';
import { slaLabel, slaTone, statusTone } from '../lib/tone.js';

export function Chip({ tone = 'flat', dot = false, icon, children, title }) {
  return (
    <span className={`chip chip--${tone}${dot ? ' chip--dot' : ''}`} title={title}>
      {icon ? (
        <span className="material-symbols-outlined" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}

/* ⚠ Priority is a square condensed BADGE, never a pill. It has to be findable
 * by shape when the queue is scanned fast, and P1 is the only solid fill so a
 * real emergency is the only thing that reads as loud. */
export function Pri({ priority, ruleName }) {
  return (
    <span
      className={`pri pri--${String(priority).toLowerCase()}`}
      title={ruleName ? `Set by rule: ${ruleName}` : undefined}
    >
      {priority}
    </span>
  );
}

export function StatusChip({ ticket }) {
  // Waiting carries its reason in the label rather than as a separate status —
  // one state to explain, and the queue still separates the two.
  const dot = ticket.status !== 'waiting';
  return (
    <Chip tone={statusTone(ticket.status)} dot={dot}>
      {statusText(ticket)}
    </Chip>
  );
}

/** A real ticket with a reply this person has not read (S10 part B). `unread` is the
 * set from lib/unread.js; null (not known, or a walkthrough build) shows nothing. */
export function NewReplyChip({ ticket, unread }) {
  if (!ticket.fromServer || !unread?.has(ticket.number)) return null;
  return (
    <Chip tone="info" dot>
      New reply
    </Chip>
  );
}

/** The queue's clock column: coloured text, no chip. */
export function SlaText({ standing, paused }) {
  if (paused) return <span className="qsla qsla--ok">Paused</span>;
  if (!standing) return null;
  const text =
    standing.state === 'met' ? 'Met' : formatRemaining(standing.remainingMins);
  return <span className={`qsla qsla--${standing.state}`}>{text}</span>;
}

/** One chip for the clock, where a chip is what the layout wants. */
export function SlaChip({ standing, label }) {
  if (!standing) return null;
  const text =
    standing.state === 'met'
      ? slaLabel(standing.state)
      : `${label ? `${label} ` : ''}${formatRemaining(standing.remainingMins)}`;
  return (
    <Chip tone={slaTone(standing.state)} icon={standing.state === 'breached' ? 'error' : 'schedule'}>
      {text}
    </Chip>
  );
}

/** Initials in a circle, coloured by who they are. */
export function Avatar({ name, kind = 'req', small = false }) {
  if (!name) {
    return (
      <span className={`av av--none${small ? ' av--sm' : ''}`} aria-hidden="true">
        ?
      </span>
    );
  }
  return (
    <span className={`av av--${kind}${small ? ' av--sm' : ''}`} title={name} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

/* Real or demo - the first thing a row should say (HD-02, #53). A real ticket is
 * saved on the server. A demo ticket is practice data that lives only in this
 * browser, and nobody in IT will ever see it. The live app shows real tickets only
 * (S08 part B), so there it says nothing. */
export function SourceChip({ ticket, walkthrough = isWalkthroughBuild() }) {
  if (!walkthrough) return null;
  return ticket.fromServer ? (
    <Chip tone="info" title="A real ticket, saved on the server.">Real</Chip>
  ) : (
    <Chip tone="warn" title="A practice ticket. It lives only in this browser.">Demo</Chip>
  );
}

/* The real tickets could not be read. Never leave this out: without it a dead
 * API looks like a quiet, ordinary queue. */
export function ServerNotice({ state, walkthrough = isWalkthroughBuild() }) {
  if (!state || state === 'ok') return null;
  // The live app has no demo tickets to fall back on, so the list is simply missing.
  const rest = walkthrough ? ' Only demo tickets are shown.' : ' Try again in a minute.';
  return (
    <div className="banner banner--bad" role="alert" style={{ margin: '12px 0' }}>
      <span className="material-symbols-outlined" aria-hidden="true">error</span>
      <span>
        {/* "real" only means something where demo tickets exist. */}
        {state === 'forbidden'
          ? (walkthrough ? `You do not have access to the real ticket list.${rest}` : 'You do not have access to the ticket list.')
          : `${walkthrough ? 'Real tickets' : 'Tickets'} could not be loaded, so this list may be missing some.${rest}`}
      </span>
      <button type="button" className="btn btn--quiet" onClick={() => refresh()}>
        Check again
      </button>
    </div>
  );
}

/* One quiet line that says what is on screen. The live app shows real tickets
 * only, so there is nothing to explain there. */
export function SourceLegend({ rows, hiddenDemo, walkthrough = isWalkthroughBuild() }) {
  if (!rows || !walkthrough) return null;
  const real = rows.filter((t) => t.fromServer).length;
  const demo = rows.length - real;
  return (
    <p className="muted" style={{ margin: '8px 0', fontSize: 12.5 }} data-testid="source-legend">
      Showing {real} real and {demo} demo {demo === 1 ? 'ticket' : 'tickets'}. Demo tickets live only in this browser.
      {hiddenDemo && hiddenDemo.length
        ? ` ${hiddenDemo.length} demo ${hiddenDemo.length === 1 ? 'ticket is' : 'tickets are'} hidden because a real ticket uses ${hiddenDemo.length === 1 ? 'its number' : 'their numbers'}: ${hiddenDemo.join(', ')}.`
        : ''}
    </p>
  );
}
